import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useTheme,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData(e.currentTarget);
      const submittedEmail = String(formData.get('email') ?? '').trim();
      const submittedPassword = String(formData.get('password') ?? '');

      await onSubmit(submittedEmail, submittedPassword);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : '로그인 처리 중 알 수 없는 오류가 발생했습니다.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="이메일 주소"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="비밀번호"
        type={showPassword ? 'text' : 'password'}
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="비밀번호 표시"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      {error && (
        <Typography 
          color="error" 
          sx={{ 
            mt: 2, 
            textAlign: 'center',
            fontSize: '0.95rem',
            color: '#fff',
            background: '#d32f2f',
            borderRadius: 1,
            py: 1
          }}
        >
          {error}
        </Typography>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{
          mt: 3,
          mb: 1,
          py: 1.5,
          backgroundColor: theme.palette.primary.main,
          color: '#fff',
          borderRadius: 2,
          fontWeight: 700,
          fontSize: '1.1rem',
          textTransform: 'none',
          boxShadow: '0 2px 8px 0 rgba(233,131,2,0.10)',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : '로그인'}
      </Button>
    </Box>
  );
};

export default LoginForm;
